import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BadgePercent, Tag } from 'lucide-react';
import { formatCurrency } from '@/lib/i18n';
import { api } from '@/lib/api';
import { unwrapList, GUEST_FALLBACK_CURRENCY } from '@/lib/guest';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { useAppSelector } from '@/Redux/store';

interface Promotion {
  id: string;
  name: string;
  description: string | null;
  type: string;
  value: string | null;
  requiresCoupon: boolean;
  startTime: string | null;
  endTime: string | null;
}

interface CouponEvaluation {
  valid: boolean;
  reason?: string;
  discount: string;
}

/**
 * What is on offer here, and whether the code in the guest's
 * pocket is worth anything. `POST /coupons/validate` is a dry run: nothing is
 * burned until the till redeems it against a check.
 */
export default function Offers() {
  const { t, i18n } = useTranslation();
  const session = useAppSelector((s) => s.guest.session);
  const [promotions, setPromotions] = useState<Promotion[]>([]);
  const [loading, setLoading] = useState(true);
  const [code, setCode] = useState('');
  const [checking, setChecking] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; text: string } | null>(null);

  const currency = session?.currency ?? GUEST_FALLBACK_CURRENCY;

  useEffect(() => {
    if (!session?.branchId) return;
    api
      .get('/promotions', { params: { branchId: session.branchId, at: new Date().toISOString() } })
      .then(({ data }) => setPromotions(unwrapList<Promotion>(data)))
      .catch(() => setPromotions([]))
      .finally(() => setLoading(false));
  }, [session?.branchId]);

  const check = async () => {
    setChecking(true);
    try {
      const { data } = await api.post<CouponEvaluation>('/coupons/validate', {
        code: code.trim(),
      });
      setResult(
        data.valid
          ? {
              ok: true,
              text: t('guest.offers.valid', {
                amount: formatCurrency(data.discount, currency, i18n.language),
              }),
            }
          : { ok: false, text: data.reason || t('guest.offers.invalid') },
      );
    } catch {
      setResult({ ok: false, text: t('guest.offers.invalid') });
    } finally {
      setChecking(false);
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="space-y-2 p-4">
          <label className="flex items-center gap-2 text-sm font-medium" htmlFor="offer-code">
            <Tag className="h-4 w-4 text-accent" />
            {t('guest.offers.codeTitle')}
          </label>
          <div className="flex gap-2">
            <Input
              id="offer-code"
              value={code}
              autoCapitalize="characters"
              onChange={(e) => {
                setCode(e.target.value);
                setResult(null);
              }}
              placeholder={t('guest.cart.couponPlaceholder')}
            />
            <Button
              variant="outline"
              disabled={!code.trim() || checking}
              onClick={() => void check()}
            >
              {t('guest.offers.check')}
            </Button>
          </div>
          {result && (
            <p className={result.ok ? 'text-sm text-positive' : 'text-sm text-negative'}>
              {result.text}
            </p>
          )}
        </CardContent>
      </Card>

      {loading ? (
        <p className="py-8 text-center text-sm text-muted-foreground">{t('common.loading')}</p>
      ) : !promotions.length ? (
        <p className="py-8 text-center text-sm text-muted-foreground">{t('guest.offers.empty')}</p>
      ) : (
        promotions.map((promotion) => (
          <Card key={promotion.id}>
            <CardContent className="flex items-start gap-3 p-4">
              <BadgePercent className="mt-0.5 h-5 w-5 shrink-0 text-accent" />
              <div className="min-w-0 space-y-1">
                <div className="font-medium">{promotion.name}</div>
                {promotion.description && (
                  <p className="text-sm text-muted-foreground">{promotion.description}</p>
                )}
                {promotion.startTime && promotion.endTime && (
                  <p className="text-xs text-muted-foreground">
                    {promotion.startTime} – {promotion.endTime}
                  </p>
                )}
                {promotion.requiresCoupon && (
                  <p className="text-xs text-muted-foreground">{t('guest.offers.needsCode')}</p>
                )}
              </div>
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );
}
